import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check current permission state
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    // Only fetch status if user is logged in (token exists)
    const token = localStorage.getItem('token');
    if (token) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/push/status');
      setPreferences(res.data.preferences);
      if (res.data.subscribed) {
        setSubscription(true);
      }
    } catch (error) {
      console.error('Failed to fetch notification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Push notifications not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        await subscribeToPush();
        return true;
      } else if (result === 'denied') {
        toast.error('Notification permission denied');
        return false;
      }
      return false;
    } catch (error) {
      console.error('Permission request failed:', error);
      toast.error('Failed to request notification permission');
      return false;
    }
  };

  const subscribeToPush = async () => {
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      console.log('Service Worker registered:', registration);

      // Get VAPID public key
      const vapidRes = await api.get('/push/vapid-public-key');
      const vapidPublicKey = vapidRes.data.publicKey;

      if (!vapidPublicKey) {
        throw new Error('VAPID public key not configured');
      }

      // Convert VAPID key
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      // Send subscription to backend
      await api.post('/push/subscribe', {
        subscription: pushSubscription.toJSON(),
        device_type: 'web'
      });

      setSubscription(pushSubscription);
      toast.success('Push notifications enabled!');
      return true;
    } catch (error) {
      console.error('Push subscription failed:', error);
      toast.error('Failed to enable push notifications');
      return false;
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const pushSubscription = await registration.pushManager.getSubscription();
      
      if (pushSubscription) {
        await pushSubscription.unsubscribe();
      }

      await api.delete('/push/unsubscribe');
      setSubscription(null);
      toast.success('Push notifications disabled');
      return true;
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      toast.error('Failed to disable push notifications');
      return false;
    }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      const res = await api.put('/push/preferences', newPrefs);
      setPreferences(res.data.preferences);
      toast.success('Notification preferences updated');
      return true;
    } catch (error) {
      console.error('Failed to update preferences:', error);
      toast.error('Failed to update preferences');
      return false;
    }
  };

  const sendTestNotification = async () => {
    try {
      const res = await api.post('/push/test', {
        title: 'Test Notification',
        body: 'This is a test push notification from Sraivest!'
      });
      
      if (res.data.success) {
        toast.success('Test notification sent!');
      } else {
        toast.error(res.data.reason || 'Failed to send test notification');
      }
      return res.data;
    } catch (error) {
      console.error('Test notification failed:', error);
      toast.error('Failed to send test notification');
      return { success: false };
    }
  };

  const value = {
    permission,
    subscription,
    preferences,
    loading,
    isSubscribed: !!subscription,
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    updatePreferences,
    sendTestNotification,
    refresh: fetchStatus
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default NotificationProvider;
